import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
  makeStyles,
  Theme,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
  FORM_HAS_ERRORS_KEY,
  IMachineUserSettingErrors,
  MAX_EMAIL_LENGTH,
  SAVE_ERROR_KEY,
  areMachineUserSettingsEqual,
  createDefaultMachineUserSetting,
  hasServiceNotificationErrors,
  validateAllNotificationEmails,
  validateNotificationEmail,
} from './ServiceDetailValidation';
import { useSelector } from 'react-redux';
import { IReduxState } from '../../types';
import { MachineUserSettingDto } from '../../rubblemaster_openapi3_typescript-fetch/models/MachineUserSettingDto';
import { machineUserSettingsApi } from '../../constants/helpers';
import { themeColors } from '../../constants/theme';

interface IProps {
  /** Persistierung. Wirft die Funktion, wird eine Fehlermeldung angezeigt. */
//   onSave: (settings: MachineUserSettingDto) => Promise<void>;
}

const useStyles = makeStyles((theme: Theme) => ({
  group: {
    marginBottom: theme.spacing(2),
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 500,
    marginBottom: theme.spacing(1.5),
  },
  groupTitle: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '1.0625rem',
    marginBottom: theme.spacing(0.5),
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  checkboxLabel: {
    color: '#fff',
    marginRight: theme.spacing(1),
    // marginTop: theme.spacing(1),
  },
  checkbox: {
    color: '#fff',
    '&.Mui-checked': { color: '#fff' },
    '&.Mui-disabled': { color: '#666' },
  },
  emailField: {
    flex: 1,
    minWidth: 320,
    maxWidth: 520,    '& .MuiInputBase-input': { color: '#fff' },
    '& .MuiInputBase-input.Mui-disabled': { color: '#888' },
    '& .MuiInputLabel-root': { color: '#bbb' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.yellow },
    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: '#666',
    },
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  saveBtn: {
    backgroundColor: themeColors.yellow,
    color: '#000',
    '&:hover': { backgroundColor: '#fff59d' }, // helleres Gelb als Hover-Ton
    '&.Mui-disabled': {
      backgroundColor: themeColors.yellow,
      color: 'rgba(0, 0, 0, 0.4)',
    },
  },
  message: {
    marginLeft: theme.spacing(2),
  },
  error: {
    color: theme.palette.error.main,
  },
}));

// export default function ServiceNotificationsTab(props: IProps) {
export default function ServiceNotificationsTab() {
  //const { onSave } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const currentUser = useSelector((state: IReduxState) => state.auth.user);
  const userId : number = currentUser?.id ?? -1;
  const userEmail: string = currentUser?.email ?? ''; 

  const machine = useSelector((state: IReduxState) => state.machineDetails.machine);
  const machineId = machine.id;
  const machineUserSetting = machine.machineUserSetting;

  const [settings, setSettings] = useState<MachineUserSettingDto>(
    () => machineUserSetting?? createDefaultMachineUserSetting(machineId, userId, userEmail)
  );
  // Letzter gespeicherter Stand - Vergleichsbasis dafür, ob es ungespeicherte
  // Änderungen gibt.
  const [lastSavedSettings, setLastSavedSettings] = useState<MachineUserSettingDto>(
    () => machineUserSetting?? createDefaultMachineUserSetting(machineId, userId, userEmail)
  );
  const [errors, setErrors] = useState<IMachineUserSettingErrors>({});
  const [saving, setSaving] = useState(false);
  const [formErrorKey, setFormErrorKey] = useState<string | undefined>(undefined);

  // Direkt aus dem aktuellen Stand abgeleitet, damit der Speichern-Button
  // sofort reagiert und nicht erst nach einem Speicherversuch.
  const hasErrors = hasServiceNotificationErrors(validateAllNotificationEmails(settings));
  const hasChanges = !areMachineUserSettingsEqual(settings, lastSavedSettings);

  // Übernimmt nachgeladene Einstellungen.
  useEffect(() => {
    if (machineUserSetting) {
      setSettings(machineUserSetting);
      setLastSavedSettings(machineUserSetting);
      setErrors({});
      setFormErrorKey(undefined);
    }
  }, [machineUserSetting]);

  const handleSave = async () => {
    setFormErrorKey(undefined);
    setSaving(true);

    try {
      // Schlüssel immer aus dem Store setzen, nicht aus dem bearbeiteten Stand.
      const machineUserSettingDto: MachineUserSettingDto = { ...settings, machineId, userId };

      // Aufruf der eighentlichen Save-Funktion
      // POST liefert den angelegten Datensatz zurück - mit den serverseitig
      // gesetzten Werten für UserEmail und den getrimmten Adressen.
      const created = await machineUserSettingsApi.apiMachineUserSettingsPost({
        machineUserSettingDto,
      });

      setSettings(created);
      setLastSavedSettings(created);
    } catch (error) {
      console.error('Speichern der Benachrichtigungseinstellungen fehlgeschlagen.', error);
      setFormErrorKey(SAVE_ERROR_KEY);
    } finally {
      setSaving(false);
    }  
  };

  return (
    <div>
      <Typography variant="subtitle1" gutterBottom className={classes.title}>
        {!!userEmail
          ? t('machine.serviceNotificationTitleWithEmail', { email: userEmail })
          : t('machine.serviceNotificationTitle')}
      </Typography>

      {/* ------------------------------------------------ 20 % Reststunden */}
      <div className={classes.group}>
        <Typography variant="subtitle2" className={classes.groupTitle}>
          {t('machine.serviceNotificationTitlePending')}
        </Typography>

        <FormControlLabel
          className={classes.checkboxLabel}
          control={
            <Checkbox
              className={classes.checkbox}
              color="default"
              checked={settings.isPendingSelf}
              onChange={(event) => {
                setSettings({
                  ...settings,
                  isPendingSelf: event.target.checked,
                });
              }}
            />
          }
          label={t('machine.serviceNotificationTextSelf')}
        />

        <div className={classes.row}>
          <FormControlLabel
            className={classes.checkboxLabel}
            control={
              <Checkbox
                className={classes.checkbox}
                color="default"
                checked={settings.isPendingOther}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setFormErrorKey(undefined);
                  // Abgewählt: Adresse leeren, damit kein verwaister Wert bleibt.
                  const email = checked ? settings.pendingOtherEmail : '';
                  setSettings({
                    ...settings,
                    isPendingOther: checked,
                    pendingOtherEmail: email,
                  });
                  // Haken gesetzt und Feld leer => Pflichtfeld-Meldung sofort zeigen.
                  // Haken entfernt => Meldung entfällt, validateNotificationEmail gibt undefined.
                  setErrors({ ...errors, pendingOtherEmail: validateNotificationEmail(email, checked) });
                }}
              />
            }
            label={t('machine.serviceNotificationTextOther')}
          />

          <TextField
            className={classes.emailField}
            variant="outlined"
            size="small"
            type="email"
            required={settings.isPendingOther}
            disabled={!settings.isPendingOther}
            placeholder={t('machine.serviceNotificationHintEmail')}
            value={settings.pendingOtherEmail}
            onChange={(event) => {
              const value = event.target.value;
              setFormErrorKey(undefined);
              setSettings({ ...settings, pendingOtherEmail: value });
              // Angezeigten Fehler entfernen, sobald die Eingabe gültig ist.
              if ( errors.pendingOtherEmail 
                && !validateNotificationEmail(value, true)
              ) {
                setErrors({ ...errors, pendingOtherEmail: undefined });
              }
            }}
            onBlur={() =>
              setErrors({
                ...errors,
                pendingOtherEmail: validateNotificationEmail(
                  settings.pendingOtherEmail,
                  settings.isPendingOther
                ),
              })
            }
            error={Boolean(errors.pendingOtherEmail)}
            helperText={errors.pendingOtherEmail ? t(errors.pendingOtherEmail) : ' '}
            autoComplete="off"
            inputProps={{ maxLength: MAX_EMAIL_LENGTH, spellCheck: false }}
          />
        </div>
      </div>

      {/* ---------------------------------------- Urgent: 0 Reststunden ---- */}
      <div className={classes.group}>
        <Typography variant="subtitle2" className={classes.groupTitle}>
          {t('machine.serviceNotificationTitleUrgent')}
        </Typography>

        <FormControlLabel
          className={classes.checkboxLabel}
          control={
            <Checkbox
              className={classes.checkbox}
              color="default"
              checked={settings.isUrgentSelf}
              onChange={(event) => {
                setSettings({ ...settings, isUrgentSelf: event.target.checked });
              }}
            />
          }
          label={t('machine.serviceNotificationTextSelf')}
        />

        <div className={classes.row}>
          <FormControlLabel
            className={classes.checkboxLabel}
            control={
              <Checkbox
                className={classes.checkbox}
                color="default"
                checked={settings.isUrgentOther}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setFormErrorKey(undefined);
                  // Abgewählt: Adresse leeren, damit kein verwaister Wert bleibt.
                  const email = checked ? settings.urgentOtherEmail : '';
                  setSettings({
                    ...settings,
                    isUrgentOther: checked,
                    urgentOtherEmail: email,
                  });
                  // Haken gesetzt und Feld leer => Pflichtfeld-Meldung sofort zeigen.
                  // Haken entfernt => Meldung entfällt, validateNotificationEmail gibt undefined.
                  setErrors({ ...errors, urgentOtherEmail: validateNotificationEmail(email, checked) });
                }}
              />
            }
            label={t('machine.serviceNotificationTextOther')}
          />

          <TextField
            className={classes.emailField}
            variant="outlined"
            size="small"
            type="email"
            required={settings.isUrgentOther}
            disabled={!settings.isUrgentOther}
            placeholder={t('machine.serviceNotificationHintEmail')}
            value={settings.urgentOtherEmail}
            onChange={(event) => {
              const value = event.target.value;
              setFormErrorKey(undefined);
              setSettings({ ...settings, urgentOtherEmail: value });
              if (errors.urgentOtherEmail && !validateNotificationEmail(value, true)) {
                setErrors({ ...errors, urgentOtherEmail: undefined });
              }
            }}
            onBlur={() =>
              setErrors({
                ...errors,
                urgentOtherEmail: validateNotificationEmail(
                  settings.urgentOtherEmail,
                  settings.isUrgentOther
                ),
              })
            }
            error={Boolean(errors.urgentOtherEmail)}
            helperText={errors.urgentOtherEmail ? t(errors.urgentOtherEmail) : ' '}
            autoComplete="off"
            inputProps={{ maxLength: MAX_EMAIL_LENGTH, spellCheck: false }}
          />
        </div>
      </div>

      {/* ------------------ Critical: Überziehung 10 %, Garantieverlust ---- */}
      <div className={classes.group}>
        <Typography variant="subtitle2" className={classes.groupTitle}>
          {t('machine.serviceNotificationTitleCritical')}
        </Typography>

        <FormControlLabel
          className={classes.checkboxLabel}
          control={
            <Checkbox
              className={classes.checkbox}
              color="default"
              checked={settings.isCriticalSelf}
              onChange={(event) => {
                setSettings({ ...settings, isCriticalSelf: event.target.checked });
              }}
            />
          }
          label={t('machine.serviceNotificationTextSelf')}
        />

        <div className={classes.row}>
          <FormControlLabel
            className={classes.checkboxLabel}
            control={
              <Checkbox
                className={classes.checkbox}
                color="default"
                checked={settings.isCriticalOther}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setFormErrorKey(undefined);
                  // Abgewählt: Adresse leeren, damit kein verwaister Wert bleibt.
                  const email = checked ? settings.criticalOtherEmail : '';
                  setSettings({
                    ...settings,
                    isCriticalOther: checked,
                    criticalOtherEmail: email,
                  });
                  // Haken gesetzt und Feld leer => Pflichtfeld-Meldung sofort zeigen.
                  // Haken entfernt => Meldung entfällt, validateNotificationEmail gibt undefined.
                  setErrors({ ...errors, criticalOtherEmail: validateNotificationEmail(email, checked) });
                }}
              />
            }
            label={t('machine.serviceNotificationTextOther')}
          />

          <TextField
            className={classes.emailField}
            variant="outlined"
            size="small"
            type="email"
            required={settings.isCriticalOther}
            disabled={!settings.isCriticalOther}
            placeholder={t('machine.serviceNotificationHintEmail')}
            value={settings.criticalOtherEmail}
            onChange={(event) => {
              const value = event.target.value;
              setFormErrorKey(undefined);
              setSettings({ ...settings, criticalOtherEmail: value });
              if (errors.criticalOtherEmail && !validateNotificationEmail(value, true)) {
                setErrors({ ...errors, criticalOtherEmail: undefined });
              }
            }}
            onBlur={() =>
              setErrors({
                ...errors,
                criticalOtherEmail: validateNotificationEmail(
                  settings.criticalOtherEmail,
                  settings.isCriticalOther
                ),
              })
            }
            error={Boolean(errors.criticalOtherEmail)}
            helperText={errors.criticalOtherEmail ? t(errors.criticalOtherEmail) : ' '}
            autoComplete="off"
            inputProps={{ maxLength: MAX_EMAIL_LENGTH, spellCheck: false }}
          />
        </div>
      </div>

      {/* --------------------------- Success: Nach Servicedurchführung ---- */}
      <div className={classes.group}>
        <Typography variant="subtitle2" className={classes.groupTitle}>
          {t('machine.serviceNotificationTitleSuccess')}
        </Typography>

        <FormControlLabel
          className={classes.checkboxLabel}
          control={
            <Checkbox
              className={classes.checkbox}
              color="default"
              checked={settings.isSuccessSelf}
              onChange={(event) => {
                setSettings({ ...settings, isSuccessSelf: event.target.checked });
              }}
            />
          }
          label={t('machine.serviceNotificationTextSelf')}
        />

        <div className={classes.row}>
          <FormControlLabel
            className={classes.checkboxLabel}
            control={
              <Checkbox
                className={classes.checkbox}
                color="default"
                checked={settings.isSuccessOther}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setFormErrorKey(undefined);
                  // Abgewählt: Adresse leeren, damit kein verwaister Wert bleibt.
                  const email = checked ? settings.successOtherEmail : '';
                  setSettings({
                    ...settings,
                    isSuccessOther: checked,
                    successOtherEmail: email,
                  });
                  // Haken gesetzt und Feld leer => Pflichtfeld-Meldung sofort zeigen.
                  // Haken entfernt => Meldung entfällt, validateNotificationEmail gibt undefined.
                  setErrors({ ...errors, successOtherEmail: validateNotificationEmail(email, checked) });
                }}
              />
            }
            label={t('machine.serviceNotificationTextOther')}
          />

          <TextField
            className={classes.emailField}
            variant="outlined"
            size="small"
            type="email"
            required={settings.isSuccessOther}
            disabled={!settings.isSuccessOther}
            placeholder={t('machine.serviceNotificationHintEmail')}
            value={settings.successOtherEmail}
            onChange={(event) => {
              const value = event.target.value;
              setFormErrorKey(undefined);
              setSettings({ ...settings, successOtherEmail: value });
              if (errors.successOtherEmail && !validateNotificationEmail(value, true)) {
                setErrors({ ...errors, successOtherEmail: undefined });
              }
            }}
            onBlur={() =>
              setErrors({
                ...errors,
                successOtherEmail: validateNotificationEmail(
                  settings.successOtherEmail,
                  settings.isSuccessOther
                ),
              })
            }
            error={Boolean(errors.successOtherEmail)}
            helperText={errors.successOtherEmail ? t(errors.successOtherEmail) : ' '}
            autoComplete="off"
            inputProps={{ maxLength: MAX_EMAIL_LENGTH, spellCheck: false }}
          />
        </div>
      </div>

      <div className={classes.footer}>
        <Button
          className={classes.saveBtn}
          onClick={handleSave}
          disabled={saving || hasErrors || !hasChanges}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : t('machine.serviceNotificationButtonSave')}
        </Button>

        {hasErrors && (
          <Typography variant="body2" className={`${classes.message} ${classes.error}`}>
            {t(FORM_HAS_ERRORS_KEY)}
          </Typography>
        )}

        {!hasErrors && formErrorKey && (
          <Typography variant="body2" className={`${classes.message} ${classes.error}`}>
            {t(formErrorKey)}
          </Typography>
        )}
      </div>
    </div>
  );
}